import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, CreditCard, Inbox as InboxIcon, Loader2, MapPin, Plus, XCircle } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";

const Approve = ({ testId, busy, onClick, label = "Approve" }) => (
  <Button data-testid={testId} size="sm" disabled={busy} onClick={onClick}
    className="bg-emerald-500 text-ink-900 hover:bg-emerald-400">
    {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
      : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}{label}
  </Button>
);

const Decline = ({ testId, busy, onClick, label = "Decline" }) => (
  <Button data-testid={testId} size="sm" variant="outline" disabled={busy} onClick={onClick}
    className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10">
    <XCircle className="mr-1.5 h-3.5 w-3.5" />{label}
  </Button>
);

const Section = ({ title, hint, count, testId, children }) => (
  <section data-testid={testId} className="space-y-3">
    <div className="flex flex-wrap items-baseline gap-3">
      <h2 className="font-display text-lg text-slate-100">{title}</h2>
      <span className="font-mono text-[11px] uppercase tracking-widest text-gold">{count}</span>
      <p className="text-sm text-slate-500">{hint}</p>
    </div>
    {children}
  </section>
);

const Row = ({ children }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-ink-600 bg-ink-800 p-5">
    {children}
  </div>
);

/** Everything waiting on a human, on one screen. */
export default function Inbox() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = () =>
    api.get("/inbox").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const act = async (key, call, done) => {
    setBusy(key); setError(""); setNotice("");
    try {
      await call();
      setNotice(done);
      await load();
      window.dispatchEvent(new Event("fm:data-changed"));
    } catch (e) {
      setError(errMsg(e));
      await load();
    } finally {
      setBusy("");
    }
  };

  if (!data) {
    return (
      <div className="grid place-items-center py-20">
        {error ? <p data-testid="inbox-error" className="text-sm text-red-400">{error}</p>
          : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  const empty = data.total === 0;

  return (
    <div data-testid="inbox-page" className="space-y-8 pb-32">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Operations</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Requests</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Everything a passenger has asked for and nobody has answered yet — pickup time changes,
          extra stops, new booking requests off your website, and any card that was declined.
        </p>
      </div>

      {notice && <p data-testid="inbox-notice" className="text-sm text-emerald-400">{notice}</p>}
      {error && <p data-testid="inbox-error" className="text-sm text-red-400">{error}</p>}

      {empty && (
        <div data-testid="inbox-empty" className="grid place-items-center rounded-xl border border-dashed border-ink-600 p-16 text-center">
          <div>
            <InboxIcon className="mx-auto h-6 w-6 text-slate-600" />
            <p className="mt-3 text-sm text-slate-400">Nothing is waiting on you. Everything is answered.</p>
          </div>
        </div>
      )}

      {(data.payments || []).length > 0 && (
        <Section testId="inbox-payments" title="Card declined" count={data.payments.length}
          hint="The balance could not be taken from the card on file — the passenger has been emailed a link.">
          {data.payments.map((r) => (
            <Row key={`p-${r.id}`}>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm text-slate-100">
                  <CreditCard className="h-4 w-4 shrink-0 text-red-300" />
                  <Link to={`/app/manifests/${r.id}`} data-testid={`inbox-payment-trip-${r.number}`}
                    className="font-mono text-gold hover:underline">#{r.number}</Link>
                  {r.customer_name}
                  <span className="text-slate-400">
                    owes <span className="font-semibold text-red-200">{money(r.amount)}</span>
                    {r.card?.last4 ? ` · ${r.card.brand || "card"} ···· ${r.card.last4}` : ""}
                  </span>
                </p>
                <p className="mt-1 truncate text-xs text-slate-500">
                  {r.error || "The card was declined."}
                  {r.customer_email ? ` · link emailed to ${r.customer_email}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <Approve testId={`inbox-payment-retry-${r.number}`} busy={busy === `p-${r.id}`}
                  label="Try the card again"
                  onClick={() => act(`p-${r.id}`,
                    () => api.post(`/manifests/${r.id}/charge-card`),
                    `${money(r.amount)} charged on trip ${r.number}.`)} />
              </div>
            </Row>
          ))}
        </Section>
      )}

      {data.changes.length > 0 && (
        <Section testId="inbox-changes" title="Pickup time changes" count={data.changes.length}
          hint="Asked for through the assistant — approving moves the trip and tells the chauffeur.">
          {data.changes.map((r) => (
            <Row key={`c-${r.id}`}>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm text-slate-100">
                  <CalendarClock className="h-4 w-4 shrink-0 text-amber-300" />
                  <Link to={`/app/manifests/${r.id}`} data-testid={`inbox-change-trip-${r.number}`}
                    className="font-mono text-gold hover:underline">#{r.number}</Link>
                  {r.customer_name}
                  <span className="text-slate-400">
                    wants <span className="font-semibold text-amber-200">{r.new_date} at {r.new_time}</span>
                    {" "}· booked {r.trip_date} at {r.pickup_time}
                  </span>
                </p>
                <p className="mt-1 truncate text-xs text-slate-500">
                  {r.pickup} → {r.dropoff}{r.chauffeur_name ? ` · ${r.chauffeur_name}` : " · unassigned"}
                </p>
              </div>
              <div className="flex gap-2">
                <Approve testId={`inbox-change-approve-${r.number}`} busy={busy === `c-${r.id}`}
                  label="Move it"
                  onClick={() => act(`c-${r.id}`,
                    () => api.post(`/manifests/${r.id}/change-request/approve`),
                    `Trip ${r.number} moved to ${r.new_date} at ${r.new_time}.`)} />
                <Decline testId={`inbox-change-decline-${r.number}`} busy={busy === `c-${r.id}`}
                  label="Keep it"
                  onClick={() => act(`c-${r.id}`,
                    () => api.post(`/manifests/${r.id}/change-request/decline`),
                    `Trip ${r.number} stays as it was — the passenger has been told.`)} />
              </div>
            </Row>
          ))}
        </Section>
      )}

      {data.stops.length > 0 && (
        <Section testId="inbox-stops" title="Extra stops" count={data.stops.length}
          hint="Added mid-trip by a passenger. Approving sends it to the chauffeur and bills it.">
          {data.stops.map((r) => (
            <Row key={`s-${r.id}-${r.index}`}>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm text-slate-100">
                  <MapPin className="h-4 w-4 shrink-0 text-gold" />
                  <Link to={`/app/manifests/${r.id}`} data-testid={`inbox-stop-trip-${r.number}`}
                    className="font-mono text-gold hover:underline">#{r.number}</Link>
                  {r.address}
                </p>
                <p className="mt-1 truncate text-xs text-slate-500">
                  {r.customer_name} · {r.trip_date} at {r.pickup_time}
                  {r.requested_by ? ` · asked by ${r.requested_by}` : ""}
                  {r.charge > 0 ? ` · ${money(r.charge)}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <Approve testId={`inbox-stop-approve-${r.number}-${r.index}`}
                  busy={busy === `s-${r.id}-${r.index}`}
                  onClick={() => act(`s-${r.id}-${r.index}`,
                    () => api.post(`/manifests/${r.id}/stops/${r.index}/approve`),
                    `Stop approved on trip ${r.number} and sent to the chauffeur.`)} />
                <Decline testId={`inbox-stop-decline-${r.number}-${r.index}`}
                  busy={busy === `s-${r.id}-${r.index}`}
                  onClick={() => act(`s-${r.id}-${r.index}`,
                    () => api.post(`/manifests/${r.id}/stops/${r.index}/decline`),
                    `Stop declined on trip ${r.number}.`)} />
              </div>
            </Row>
          ))}
        </Section>
      )}

      {data.bookings.length > 0 && (
        <Section testId="inbox-bookings" title="Booking requests" count={data.bookings.length}
          hint="From your booking page. Accepting needs a vehicle and a price, so it opens Bookings.">
          {data.bookings.map((r) => (
            <Row key={`b-${r.id}`}>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm text-slate-100">
                  <Plus className="h-4 w-4 shrink-0 text-gold" />
                  {r.customer_name || "A passenger"}
                  <span className="text-slate-400">
                    {r.trip_date} at {r.pickup_time}
                    {r.estimated_total > 0 ? ` · about ${money(r.estimated_total)}` : ""}
                  </span>
                </p>
                <p className="mt-1 truncate text-xs text-slate-500">
                  {r.pickup} → {r.dropoff}{r.customer_phone ? ` · ${r.customer_phone}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                <Button asChild size="sm" className="bg-gold text-ink-900 hover:bg-gold-light">
                  <Link to="/app/bookings" data-testid={`inbox-booking-open-${r.id}`}>
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Accept &amp; price
                  </Link>
                </Button>
                <Decline testId={`inbox-booking-decline-${r.id}`} busy={busy === `b-${r.id}`}
                  onClick={() => act(`b-${r.id}`,
                    () => api.post(`/inbox/bookings/${r.id}/decline`),
                    "Booking request declined.")} />
              </div>
            </Row>
          ))}
        </Section>
      )}
    </div>
  );
}
